import { MRT_Localization_EN } from "material-react-table/locales/en";
import { MRT_Localization_PL } from "material-react-table/locales/pl";
import React from "react";
import { useTranslation } from "react-i18next";
import { useShallow } from "zustand/shallow";
import { useRequestsQueueStatePersistedStore } from "../store";

const mrtLocales = {
  pl: MRT_Localization_PL,
  en: MRT_Localization_EN,
};

/**
 * A custom hook to manage the default options and state for a material-react-table
 * used in displaying request queues.
 *
 * @param {string} queue - The queue identifier used to get and set the state from the store.
 * @returns {Object} The configuration options and state handlers for the table.
 */
export function useDefaultRequestsTableOptions(queue) {
  const { i18n } = useTranslation();

  const [queueState, setQueueState] = useRequestsQueueStatePersistedStore(
    useShallow((state) => [state.queuesState[queue], state.setQueueState])
  );

  const [density, setDensity] = React.useState(queueState.density);
  const [sorting, setSorting] = React.useState(queueState.sorting);
  const [pagination, setPagination] = React.useState(queueState.pagination);
  const [columnVisibility, setColumnVisibility] = React.useState(
    queueState.columnVisibility
  );
  const [columnPinning, setColumnPinning] = React.useState(
    queueState.columnPinning
  );

  React.useEffect(() => {
    setQueueState(queue, {
      pagination,
      sorting,
      density,
      columnVisibility,
      columnPinning,
    });
  }, [
    pagination.pageIndex,
    pagination.pageSize,
    sorting,
    queue,
    density,
    columnVisibility,
    columnPinning,
  ]);

  return {
    state: {
      pagination,
      sorting,
      density,
      columnVisibility: {
        "mrt-row-expand": false,
        ...columnVisibility,
      },
      columnPinning: {
        right: ["mrt-row-actions", ...columnPinning.right],
        left: [...columnPinning.left],
      },
    },
    initialState: {
      showGlobalFilter: true,
    },

    enableFacetedValues: true,
    localization: mrtLocales[i18n.language],
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    onDensityChange: setDensity,
    onColumnVisibilityChange: setColumnVisibility,
    onColumnPinningChange: setColumnPinning,
    positionGlobalFilter: "left",
    enableStickyHeader: true,
    muiTopToolbarProps: {
      sx: {
        borderBottom: "1px solid rgba(224, 224, 224, 1)",
      },
    },
    muiPaginationProps: {
      rowsPerPageOptions: [10, 20, 50],
      SelectProps: {
        sx: { fontSize: "14px" },
      },
    },
    displayColumnDefOptions: {
      "mrt-row-expand": {
        visibleInShowHideMenu: false,
      },
      "mrt-row-actions": {
        visibleInShowHideMenu: false,
      },
    },
    muiTablePaperProps: ({ table }) => ({
      //not sx
      style: {
        zIndex: table.getState().isFullScreen ? 1150 : undefined,
      },
    }),
    muiTableBodyRowProps: ({ row }) => ({
      sx: {
        backgroundColor: row.original.is_vip ? "#ffd7004d" : undefined,
      },
    }),
    muiDetailPanelProps: {
      sx: {
        backgroundColor: "#fff",
      },
    },
    // without "renderTopToolbarCustomActions: () => null" error is displayed directly on header ¯\_(ツ)_/¯
    renderTopToolbarCustomActions: () => null,
  };
}
